'use client';

/**
 * Safe JSON-LD script component that avoids dangerouslySetInnerHTML
 *
 * Uses useEffect to inject the script tag safely via the DOM API,
 * preventing XSS attacks while ensuring JSON-LD is available for SEO.
 *
 * Modern search engines (Google, Bing) execute JavaScript and will
 * see the JSON-LD script, so this approach is SEO-friendly.
 *
 * Security: The data prop is serialized using JSON.stringify() which properly
 * escapes all special characters. The script is injected using textContent
 * (not innerHTML), which automatically escapes HTML entities.
 */

import { useEffect, useRef } from 'react';

interface JSONLDScriptProps {
  /** The JSON-LD schema object to inject */
  data: object;
  /** Optional ID for the script tag (useful for debugging) */
  id?: string;
}

export function JSONLDScript({ data, id }: JSONLDScriptProps) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const jsonString = JSON.stringify(data);

  useEffect(() => {
    // Check if script already exists to avoid duplicates
    const existingScript = id ? document.getElementById(id) : null;
    if (existingScript) {
      // Update existing script if data changed
      if (existingScript.textContent !== jsonString) {
        existingScript.textContent = jsonString;
      }
      scriptRef.current = existingScript as HTMLScriptElement;
      return;
    }

    // Create script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    if (id) {
      script.id = id;
    }

    // Safely set JSON content using textContent (automatically escapes HTML)
    // JSON.stringify ensures proper escaping of special characters
    script.textContent = jsonString;

    // Append to document head
    document.head.appendChild(script);
    scriptRef.current = script;

    // Cleanup: remove script when component unmounts
    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [id, jsonString]); // Depend on both id and jsonString

  // Return null since we're injecting the script manually
  return null;
}
